import cn from 'classnames';
import { FC } from 'react';

import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import type {
  DocumentSectionSpec,
  DocumentTemplate,
} from '@/entities/templates';

import {
  getStructureItemId,
  listNoteLineOpeners,
  listNoteOptionLines,
  listShownLineOpeners,
  nameNoteLinkOpener,
  type NamedNoteLinkOpener,
  type SurfaceSection,
} from '../lib';

import { LinkedLineMark } from './LinkedLineMark';
import styles from './NoteSection.module.scss';
import { NoteOptionLinksContext, TemplateLineView } from './TemplateFieldView';
import { SectionShell } from './TemplateSection';

type Props = {
  // The template's section: absent only for a section scribe added.
  section: DocumentSectionSpec | undefined;
  // The template it is read from, whose links it shows.
  template: DocumentTemplate;
  surface: SurfaceSection;
  lists: NoteList[];
  notation?: NoteToothNotation;
  // The template's language, which the note is printed in.
  language?: string;
  className?: string;
  onShowOpener: (opener: NamedNoteLinkOpener) => void;
};

// A section of a block template as its note reads, on the Templates page:
// its lines as sentences, each link on both of its ends: the chain after an
// option that opens lines, and a linked line laid out as the others with a
// rule on its left and "↳ option" after its text, which leads back to the
// option. The template itself is edited as one document.
export const NoteSection: FC<Props> = ({
  section,
  template,
  surface,
  lists,
  notation,
  language,
  className,
  onShowOpener,
}) => {
  const title = surface.title || section?.Title || '';

  const opens = (blockId: string, value: string) =>
    listNoteOptionLines(template, blockId, value).length;

  return (
    <NoteOptionLinksContext.Provider value={opens}>
      <SectionShell
        sortableId={getStructureItemId({ kind: 'section', code: surface.code })}
        editable={false}
        dropDisabled
        title={title}
        className={className}
        sectionCode={surface.code}
        sectionId={surface.id}
      >
        <div className={styles.lines}>
          {surface.lines.map((line) => {
            const openers = listNoteLineOpeners(template, line.id);
            return (
              <div
                key={line.id}
                className={cn(
                  styles.row,
                  openers.length > 0 && styles.linkedRow,
                )}
                data-component="workspace-template-line"
                data-line-id={line.id}
                data-status={line.state}
              >
                <TemplateLineView
                  className={styles.text}
                  line={line}
                  mode="prefill"
                  lists={lists}
                  notation={notation}
                  language={language}
                />
                <LinkedLineMark
                  openers={listShownLineOpeners(
                    openers.map((opener) => ({
                      ...opener,
                      label: nameNoteLinkOpener(template, opener),
                    })),
                  )}
                  dataComponent="workspace-template-linked-label"
                  onSelect={onShowOpener}
                />
              </div>
            );
          })}
        </div>
      </SectionShell>
    </NoteOptionLinksContext.Provider>
  );
};
